import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Layers, Plus, Trash2, Pencil, Lock, ArrowRight } from "lucide-react";

export default function Workspaces() {
  const nav = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const { workspaces, activeId, canCreateMore, planTier, create, rename, remove, switchTo } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await create(newName.trim());
      await refreshUser();
      toast.success("Workspace created. Let's onboard it.");
      nav("/onboarding");
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || "Could not create workspace"));
    } finally { setBusy(false); }
  };

  const onRename = async (id) => {
    if (!editName.trim()) { setEditing(null); return; }
    setBusy(true);
    try { await rename(id, editName.trim()); setEditing(null); toast.success("Renamed"); }
    catch { toast.error("Could not rename"); }
    finally { setBusy(false); }
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete workspace "${name}"? This removes its Dive Brain and vault items. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await remove(id);
      await refreshUser();
      toast.success("Workspace deleted");
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || "Could not delete"));
    } finally { setBusy(false); }
  };

  const onOpen = async (id) => {
    if (id === activeId) { nav("/dashboard"); return; }
    setBusy(true);
    try {
      const res = await switchTo(id);
      await refreshUser();
      if (res && !res.onboarded) nav("/onboarding");
      else window.location.assign("/dashboard");
    } catch { toast.error("Could not switch workspace"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-10 py-8 lg:py-12" data-testid="workspaces-page">
      <div className="flex items-start gap-3 mb-8">
        <div className="w-10 h-10 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center">
          <Layers className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl text-[#0A2342] leading-tight">Workspaces</h1>
          <p className="text-[15px] text-[#475569] mt-1 max-w-2xl">
            One workspace = one Dive Brain (identity, voice, vault). Perfect for running a personal creator account and a dive business side by side without their voices bleeding into each other.
          </p>
        </div>
      </div>

      {!canCreateMore && planTier !== "pro_unlimited" && (
        <div data-testid="workspaces-upgrade-banner" className="mb-6 dms-card p-5 border-[#0A2342]/15 bg-gradient-to-br from-[#0A2342] to-[#1D4E89] text-white">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-[#4FD1C5] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-bold text-lg">Unlock multi-workspace</div>
              <p className="text-[13.5px] text-white/85 mt-1">
                Pro Unlimited gives you unlimited workspaces, unlimited strategy credits, and every tool. Ideal if you run a creator brand and a dive centre — or manage two locations.
              </p>
              <button onClick={() => nav("/billing")} data-testid="workspaces-upgrade-btn" className="mt-3 inline-flex items-center gap-1.5 bg-[#4FD1C5] text-[#0A2342] font-heading font-bold text-[13px] px-4 py-2 rounded-md hover:bg-white transition-colors">
                See Pro Unlimited <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dms-card p-0 overflow-hidden">
        <div className="divide-y divide-[#F1F5F9]">
          {workspaces.map((w) => (
            <div key={w.id} data-testid={`workspace-row-${w.id}`} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 rounded bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center font-heading font-bold text-[13px] shrink-0">
                {(w.name || "W").split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase()).join("")}
              </div>
              <div className="flex-1 min-w-0">
                {editing === w.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                      maxLength={60}
                      data-testid={`workspace-rename-input-${w.id}`}
                      className="flex-1 text-[14px] px-2.5 py-1.5 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#4FD1C5]"
                    />
                    <button onClick={() => onRename(w.id)} data-testid={`workspace-rename-save-${w.id}`} className="dms-btn-primary px-3 py-1.5 text-[12px]">Save</button>
                    <button onClick={() => setEditing(null)} className="text-[12px] text-[#64748B]">Cancel</button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-bold text-[16px] text-[#0A2342]">{w.name}</div>
                      {w.id === activeId && <span className="dms-badge bg-[#0F766E]/10 text-[#0F766E] border-[#0F766E]/20">Active</span>}
                      {!w.onboarded && <span className="dms-badge bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/20">Needs onboarding</span>}
                    </div>
                    <div className="text-[12px] text-[#64748B] mt-0.5">
                      Created {new Date(w.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </>
                )}
              </div>
              {editing !== w.id && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => onOpen(w.id)} disabled={busy} data-testid={`workspace-open-${w.id}`} className="text-[12.5px] font-semibold text-[#0A2342] px-3 py-1.5 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9]">
                    {w.id === activeId ? "Open" : "Switch to"}
                  </button>
                  <button onClick={() => { setEditing(w.id); setEditName(w.name); }} disabled={busy} data-testid={`workspace-rename-${w.id}`} title="Rename" className="p-2 rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0A2342]">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {workspaces.length > 1 && (
                    <button onClick={() => onDelete(w.id, w.name)} disabled={busy} data-testid={`workspace-delete-${w.id}`} title="Delete" className="p-2 rounded-md text-[#64748B] hover:bg-[#FEF2F2] hover:text-[#B91C1C]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {creating ? (
          <form onSubmit={onCreate} className="p-5 border-t border-[#F1F5F9] bg-[#F8FAFC] flex items-center gap-2">
            <input
              autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
              maxLength={60} placeholder="e.g. Freediving Brand"
              data-testid="workspaces-new-name-input"
              className="flex-1 text-[14px] px-3 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#4FD1C5]"
            />
            <button type="submit" disabled={busy || !newName.trim()} data-testid="workspaces-new-submit" className="dms-btn-primary px-4 py-2 text-[13px]">Create</button>
            <button type="button" onClick={() => { setCreating(false); setNewName(""); }} className="text-[13px] text-[#64748B] px-2 py-2 hover:text-[#0A2342]">Cancel</button>
          </form>
        ) : canCreateMore ? (
          <button onClick={() => setCreating(true)} data-testid="workspaces-add-button" className="w-full p-4 border-t border-[#F1F5F9] flex items-center justify-center gap-2 text-[13.5px] font-semibold text-[#0A2342] hover:bg-[#F1F5F9]">
            <Plus className="w-4 h-4" /> Add another workspace
          </button>
        ) : null}
      </div>
    </div>
  );
}
